import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const DEVICES = { desktop: null, tablet: { w: 820, h: 1180, label: 'iPad 820×1180' }, mobile: { w: 390, h: 844, label: 'iPhone 390×844' } };

const SRC = '<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width, initial-scale=1"></head><body></body></html>';

/** Copies the app stylesheets into the iframe so Tailwind breakpoints resolve against the device width. */
const syncStyles = (doc) => {
  const head = doc.head;
  const seen = doc.__pillotSynced || (doc.__pillotSynced = new Set());
  document.head.querySelectorAll('link[rel="stylesheet"], link[rel="preconnect"], style').forEach((node) => {
    const key = node.href || node.textContent;
    if (seen.has(key)) return;
    seen.add(key);
    head.appendChild(node.cloneNode(true));
  });
  if (!doc.getElementById('frame-base')) {
    const s = doc.createElement('style');
    s.id = 'frame-base';
    s.textContent = 'html,body{margin:0;min-height:100%;background:#fff}body{-webkit-font-smoothing:antialiased}';
    head.appendChild(s);
  }
};

/**
 * Renders children into a real iframe sized like the chosen device, so media queries,
 * sticky headers and scrolling behave exactly like on a phone/tablet.
 */
export const DeviceFrame = ({ device, children, onDocument }) => {
  const spec = DEVICES[device];
  const ref = useRef(null);
  const [doc, setDoc] = useState(null);
  const onLoad = useCallback(() => {
    const d = ref.current?.contentDocument;
    if (!d) return;
    syncStyles(d);
    setDoc(d);
    onDocument?.(d);
  }, [onDocument]);
  useEffect(() => {
    setDoc(null);
    if (!spec) { onDocument?.(null); return undefined; }
    const obs = new MutationObserver(() => { if (ref.current?.contentDocument) syncStyles(ref.current.contentDocument); });
    obs.observe(document.head, { childList: true });
    return () => obs.disconnect();
  }, [spec, onDocument]);
  if (!spec) return children;
  const mobile = device === 'mobile';
  return (
    <div className="mx-auto flex h-full flex-col items-center" data-testid={`device-frame-${device}`}>
      <div className={`relative flex min-h-0 flex-1 flex-col bg-slate-900 shadow-2xl ${mobile ? 'rounded-[2.4rem] p-2.5' : 'rounded-[1.6rem] p-3'}`} style={{ width: spec.w + (mobile ? 20 : 24), maxWidth: '100%', maxHeight: spec.h + (mobile ? 20 : 24) }}>
        {mobile && <div className="pointer-events-none absolute left-1/2 top-2.5 z-10 h-6 w-28 -translate-x-1/2 rounded-b-2xl bg-slate-900" />}
        <iframe key={device} ref={ref} title={`preview-${device}`} srcDoc={SRC} onLoad={onLoad} className={`block w-full flex-1 bg-white ${mobile ? 'rounded-[2rem]' : 'rounded-[1.1rem]'}`} style={{ border: 0, minHeight: 320 }} data-testid={`device-frame-iframe-${device}`} />
        {doc && createPortal(children, doc.body)}
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground" data-testid="device-frame-label">{spec.label}</div>
    </div>
  );
};
